import * as React from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import { usePortalScope } from "@/analytics/PortalScope";

export interface PromptDialogProps {
  open: boolean;
  title: string;
  label?: string;
  /** Initial field value. */
  initialValue?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Receives the entered value. May be async; a throw shows inline + keeps
   *  the dialog open. The caller closes on success. */
  onConfirm: (value: string) => void | Promise<void>;
  onClose: () => void;
  /** Analytics tag scope, as a full path (the dialog is portaled out of its
   *  caller's DOM, like the ui Menu's `scope`), e.g. "projects-actions-rename_dialog".
   *  Defaults to the route's area + "prompt_dialog", so its buttons read
   *  `<area>-prompt_dialog-submit` / `-cancel`. Callers pass their own, e.g.
   *  `usePortalScope("actions-rename_dialog")["data-ph-scope"]`, so two dialogs
   *  on one route never share a path. */
  scope?: string;
}

/**
 * In-page single-text-input dialog — the replacement for `window.prompt`.
 * Submits on Enter, shows a spinner while the async action runs, and surfaces
 * thrown errors inline.
 */
export function PromptDialog({
  open,
  title,
  label,
  initialValue = "",
  confirmLabel = "Save",
  cancelLabel = "Cancel",
  onConfirm,
  onClose,
  scope,
}: PromptDialogProps) {
  const defaultScope = usePortalScope("prompt_dialog");
  const paperScope = scope ? { "data-ph-scope": scope } : defaultScope;
  const [value, setValue] = React.useState(initialValue);
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) {
      setValue(initialValue);
      setBusy(false);
      setError(null);
    }
  }, [open, initialValue]);

  const handleConfirm = async () => {
    const v = value.trim();
    if (!v) {
      setError("Please enter a value.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onConfirm(v);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth slotProps={{ paper: paperScope }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          label={label}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") { e.preventDefault(); handleConfirm(); }
          }}
          sx={{ mt: 1 }}
        />
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy} data-ph="cancel">{cancelLabel}</Button>
        <Button
          onClick={handleConfirm}
          data-ph="submit"
          disabled={busy}
          variant="contained"
          startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
